import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from 'src/app/core/services/api/configuration.service';
import { QueDatePipe } from 'src/app/pipes/date.pipe';
import { ManipulationsToolbarStore } from 'src/app/store/manipulations-toolbar-store';
import { environment } from 'src/environment';
import type { Swiper } from 'swiper/types';
import type { SwiperContainer } from 'swiper/element';
import { parseToLocalDate } from '../../utils/date.utils';

interface ICalendarDateItem {
  month: {
    en: string;
    he: string;
  };
  monthNum: number;
  isCurrentMonth: boolean;
  currentMonthNumberInSlides: number;
  year: number;
  days: ICalendarDayItem[];
}
interface ICalendarDayItem {
  isRelatedToMonth: boolean;
  isSingalDateSelected: boolean;
  isSelected: boolean;
  isToday: boolean;
  isStart: boolean;
  isEnd: boolean;
  date: Date;
  isDisable: boolean;
}

export interface ICalendarResults {
  startDate?: Date | null;
  endDate?: Date | null;
}

@Component({
    selector: 'que-dual-calendar-range-picker',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        OverlayModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatDatepickerModule,
        TranslateDirective, TranslatePipe,
        QueDatePipe,
    ],
    // <swiper-container>/<swiper-slide> are native custom elements (registered via
    // register() in main.ts), not Angular directives - the compiler needs this schema
    // to accept their unrecognized tag/attribute names.
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './dual-calendar-range-picker.component.html',
    styleUrls: ['./dual-calendar-range-picker.component.scss']
})
export class DualCalendarRangePickerComponent implements OnInit {
  #manipulationStore = inject(ManipulationsToolbarStore);

  dateControl = new FormControl<Date>(new Date());
  @Input() isSingalDate: boolean = false;
  @Input() isPreviewTodayDisable: boolean = false;
  @Input() customDateFrom?: Date | null;
  @Input() customDateTo?: Date | null;
  @Input() maxSelectableDate?: Date | null;
  rangeDateToStart = new Date();
  // The number of months forward
  @Input('rangeNumberOfMonthForward') rangeNumberOfMonthForward?: number | null;

  // Emits the change event (can return null in the dates)
  @Output('rangeChanged') rangePickedEmitter =
    new EventEmitter<ICalendarResults>();
  @ViewChild('swiper') swiperEl!: ElementRef<SwiperContainer>;
  @ViewChild('monthList') monthList!: ElementRef;
  @ViewChild('yearList') yearList!: ElementRef;

  #translate = inject(TranslateService);
  readonly positonArea = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    },
  ] as ConnectedPosition[];
  isMonthsToSelectOpen: boolean[] = Array(10).fill(false);
  isYearsToSelectOpen: boolean[] = Array(10).fill(false);
  showMonthPicker: boolean = false;
  showYearPicker: boolean = false;
  showDayPicker: boolean = true;
  monthDic: { en: string; he: string }[] = [
    { en: 'January', he: 'ינואר' },
    { en: 'February', he: 'פברואר' },
    { en: 'March', he: 'מרץ' },
    { en: 'April', he: 'אפריל' },
    { en: 'May', he: 'מאי' },
    { en: 'June', he: 'יוני' },
    { en: 'July', he: 'יולי' },
    { en: 'August', he: 'אוגוסט' },
    { en: 'September', he: 'ספטמבר' },
    { en: 'October', he: 'אוקטובר' },
    { en: 'November', he: 'נובמבר' },
    { en: 'December', he: 'דצמבר' },
  ];
  selectedMonth!: ICalendarDateItem;
  yearNames: number[] = [];
  years: number[] = [];
  months = [];
  rangeMonthNames: ICalendarDateItem[] = [];
  startDate?: Date | null;
  endDate?: Date | null;
  swiperRef?: Swiper;
  swiperIsEnd: boolean = false;
  swiperIsBeginning: boolean = false;
  minDate = new Date(inject(ConfigurationService).config.minDateForRangeInput);
  selectedStartDate?: Date | null;
  selectedEndDate?: Date | null;
  chooseDay = true;
  countPrev = 10;
  valueOfMonthSelect!: {
    en: string;
    he: string;
  };
  valueOfYearSelect!: number;
  constructor(private ref: ChangeDetectorRef) {}
  isCustomDateFromNull = false;
  lang = environment.lang;
  ngOnInit(): void {
    if (!this.isSingalDate) {
      if (!this.customDateFrom) {
        this.isCustomDateFromNull = true;
      } else {
        this.startDate = this.customDateFrom;
      }
      if (this.customDateTo) {
        this.endDate = this.customDateTo;
      }
    }

    this.customDateFrom = this.customDateFrom
      ? parseToLocalDate(this.customDateFrom)
        ? parseToLocalDate(this.customDateFrom)!
        : new Date()
      : new Date();
    this.customDateTo = this.customDateTo
      ? parseToLocalDate(this.customDateTo)
      : null;
    this.selectedMonth = {
      month: this.monthDic[this.customDateFrom.getMonth()],
      monthNum: this.customDateFrom.getMonth() + 1,
      isCurrentMonth: false,
      currentMonthNumberInSlides: 0,
      year: this.customDateFrom.getFullYear(),
      days: [],
    };
    this.valueOfMonthSelect = this.selectedMonth.month;
    this.valueOfYearSelect = this.selectedMonth.year;
    // Start the range 10 months back
    this.rangeDateToStart = new Date(this.customDateFrom);
    this.rangeDateToStart.setMonth(this.customDateFrom.getMonth() - 10);
    setTimeout(() => {
      this.swiperRef = this.swiperEl.nativeElement.swiper;
    }, 1);
    this.changeCalendarValues();
    for (
      let i = this.minDate.getFullYear();
      i <= new Date().getFullYear() + 20;
      i++
    ) {
      this.yearNames.push(i);
    }
  }

  private generateMonthArray(quantityOfMonths: number) {
    var dateForm = this.rangeDateToStart;
    let monthArray = [];
    let fromMonth = dateForm.getMonth();
    let fromYear = dateForm.getFullYear();
    // to add days oneMonth
    const daysInView = 35;
    for (let i = 0; i < quantityOfMonths; i++) {
      let daysInMonth = new Date(fromYear, fromMonth + 1, 0).getDate();
      const days = [];
      const daysOffset = daysInView - daysInMonth;
      const day = new Date(fromYear, fromMonth, 1);
      const current = day.getDay();
      let isThisTheSelectedMonth = false;
      let isThisTheTodayMonth = false;
      // Add days from the previous month
      if (current > 0) {
        for (let j = current - 1; j >= 0; j--) {
          let prevMonth = fromMonth - 1;
          let prevYear = fromYear;
          if (prevMonth < 0) {
            prevMonth = 11;
            prevYear--;
          }
          const prevMonthDays = new Date(prevYear, prevMonth + 1, 0).getDate();
          const dayDate = new Date(prevYear, prevMonth + 1, 0);

          dayDate.setDate(prevMonthDays - j);
          const isDisableDate = this.shouldDisableDate(dayDate);

          days.push({
            isRelatedToMonth: false,
            isToday: false,
            isSingalDateSelected: false,
            isSelected: false,
            isStart: false,
            isEnd: false,
            date: new Date(dayDate),
            isDisable: isDisableDate,
          });
        }
      }

      // Add days from the current month
      for (let j = 1; j <= daysInMonth; j++) {
        const dayDate = new Date(fromYear, fromMonth + 1, 0);
        dayDate.setDate(j);
        const isDisableDate = this.shouldDisableDate(dayDate);
        const sameSelectedDay = this.sameDay(dayDate, this.customDateFrom!);
        const sameDay = this.sameDay(dayDate, new Date());

        if (!isThisTheTodayMonth) {
          isThisTheTodayMonth = sameDay;
        }
        if (!isThisTheSelectedMonth) {
          isThisTheSelectedMonth = sameSelectedDay;
        }
        days.push({
          isRelatedToMonth: true,
          isToday: sameDay,
          isSelected: sameSelectedDay,
          isSingalDateSelected: false,
          isStart:
            !this.customDateFrom && !this.customDateTo
              ? false
              : this.sameDay(dayDate, this.customDateFrom!),

          isEnd:
            !this.customDateFrom && !this.customDateTo
              ? false
              : this.sameDay(dayDate, this.customDateTo!),

          date: new Date(dayDate),
          isDisable: isDisableDate,
        });
      }

      // Add days from the next month
      if (days.length < daysInView) {
        let nextMonth = fromMonth + 2;
        let nextYear = fromYear;
        if (nextMonth > 11) {
          nextMonth = 0;
          nextYear++;
        }
        const loopToComplete = daysInView - days.length;
        for (let j = 1; j <= loopToComplete; j++) {
          const dayDate = new Date(nextYear, nextMonth, 0);

          dayDate.setDate(j);
          const isDisableDate = this.shouldDisableDate(dayDate);

          days.push({
            isRelatedToMonth: false,
            isToday: false,
            isSelected: false,
            isSingalDateSelected: false,
            isStart: false,
            isEnd: false,
            date: new Date(dayDate),
            isDisable: isDisableDate,
          });
        }
      }

      monthArray.push({
        month: this.monthDic[fromMonth],
        monthNum: fromMonth + 1,
        isCurrentMonth: isThisTheSelectedMonth,
        currentMonthNumberInSlides: monthArray.length,
        year: fromYear,
        days: days,
      });

      fromMonth++;
      if (fromMonth > 11) {
        fromMonth = 0;
        fromYear++;
      }
    }
    this.last = this.countPrev;
    return monthArray;
  }

  selectRange(startDate: Date, endDate: Date | null) {
    for (let month of this.rangeMonthNames) {
      for (let day of month.days) {
        let dayDate = new Date(day.date);
        if (
          this.sameDay(dayDate, startDate) ||
          this.sameDay(dayDate, endDate)
        ) {
          day.isSelected = true;
          if (this.isSingalDate) {
            day.isSingalDateSelected = true;
          } else {
            if (this.sameDay(dayDate, startDate)) {
              day.isStart = true;
              this.selectedStartDate = new Date(day.date);
            }

            if (this.sameDay(dayDate, endDate)) {
              day.isEnd = true;
              this.selectedEndDate = new Date(day.date);
            }
          }
        } else {
          day.isSingalDateSelected = false;
          day.isSelected = false;
          day.isStart = false;
          day.isEnd = false;
        }
      }
    }

    const resultOut: ICalendarResults = {
      startDate: this.selectedStartDate || this.startDate,
      endDate: this.selectedEndDate || this.endDate,
    };

    // if (fullRangeInArgs) {
    //   this.startDate = null;
    //   this.endDate = null;
    // }
    this.rangePickedEmitter.emit(resultOut);
  }

  selectDatesForRange(day: ICalendarDayItem) {
    if (day.isDisable) {
      return;
    }
    if (this.isSingalDate) {
      this.startDate = day.date;
      this.selectRange(new Date(this.startDate), null);
      this.rangePickedEmitter.emit({
        startDate: this.startDate,
      });
    } else {
      if (!this.startDate || this.isCustomDateFromNull) {
        this.startDate = day.date;
        this.customDateFrom = day.date;
      } else {
        if (!this.endDate) {
          this.endDate = day.date;
        } else {
          this.startDate = day.date;
          this.endDate = null;
        }
      }
      this.selectRange(
        new Date(this.startDate),
        this.endDate ? new Date(this.endDate) : null,
      );

      if (this.startDate) {
        this.rangeMonthNames.forEach((rangeMonth) => {
          rangeMonth.days.forEach((days) => {
            days.isDisable = this.startDate
              ? days.date < this.startDate
              : false;
          });
        });
      }
    }
    this.isCustomDateFromNull = false;
  }
  begin = false;
  updateSelect(where: 'next' | 'prev') {
    this.begin = false;
    if (where == 'next') {
      if (
        this.monthDic.indexOf(this.valueOfMonthSelect) ==
        this.monthDic.length - 1
      ) {
        this.valueOfMonthSelect = this.monthDic[0];
        var yearSelect = +this.valueOfYearSelect;
        this.valueOfYearSelect =
          this.yearNames[this.yearNames.indexOf(+this.valueOfYearSelect) + 1];
        this.selectedMonth.year = this.valueOfYearSelect;
        if (!this.valueOfYearSelect) {
          this.yearNames = [...this.yearNames, yearSelect + 1];
          this.valueOfYearSelect = yearSelect + 1;
        }
      } else {
        this.valueOfMonthSelect =
          this.monthDic[this.monthDic.indexOf(this.valueOfMonthSelect) + 1];
      }
    } else {
      if (
        this.valueOfMonthSelect == this.monthDic[this.minDate.getMonth() + 1] &&
        +this.valueOfYearSelect == this.minDate.getFullYear()
      ) {
        this.begin = true;
      }
      if (this.monthDic.indexOf(this.valueOfMonthSelect) == 0) {
        this.valueOfMonthSelect = this.monthDic[this.monthDic.length - 1];
        var yearSelect = +this.valueOfYearSelect;
        this.valueOfYearSelect =
          this.yearNames[this.yearNames.indexOf(+this.valueOfYearSelect) - 1];
        if (!this.valueOfYearSelect) {
          this.yearNames = [yearSelect - 1, ...this.yearNames];
          this.valueOfYearSelect = yearSelect - 1;
        }
        this.selectedMonth.year = this.valueOfYearSelect;
      } else {
        this.valueOfMonthSelect =
          this.monthDic[this.monthDic.indexOf(this.valueOfMonthSelect) - 1];
      }
    }
  }

  moveSlides(where: 'next' | 'prev') {
    //this function dont work well...
    const lang = this.#translate.instant('header.langCode'); //this code incorrect -> get lang = 'he' when lang is 'en'
    if (
      (where == 'next' && lang == 'he') ||
      (where == 'prev' && lang == 'en')
    ) {
      this.swiperEl.nativeElement.swiper.slideNext();
    } else {
      this.swiperEl.nativeElement.swiper.slidePrev();
    }
    this.monthChanged(this.swiperEl.nativeElement.swiper);
  }

  onSlideChange(event: Event) {
    this.monthChanged((event as CustomEvent).detail[0]);
  }

  last: number = 10;
  monthChanged(swip: Swiper, flag: boolean = true) {
    this.swiperIsEnd = swip.isEnd;
    this.swiperIsBeginning = swip.isBeginning;
    let where: 'next' | 'prev' | null = null;
    if (flag && this.last) {
      if (swip.activeIndex < this.last) {
        where = 'prev';
      } else if (swip.activeIndex > this.last) {
        where = 'next';
      }
      if (where) {
        this.updateSelect(where);
      }
      this.last = swip.activeIndex;
    }
    this.swiperRef = swip;
    this.ref.detectChanges();
    this.countPrev = swip.activeIndex;
  }

  changeCalendarValues() {
    const currentDate = this.rangeDateToStart || new Date();
    this.rangeMonthNames = this.generateMonthArray(
      this.rangeNumberOfMonthForward || 20,
    );
    // Slide to the current month
    setTimeout(() => {
      for (let month of this.rangeMonthNames) {
        if (month.isCurrentMonth) {
          this.swiperRef?.slideTo(month.currentMonthNumberInSlides, 0);
        }
      }
    }, 50);
  }

  sameDay(date1: Date | null, date2: Date | null) {
    if (!date1 || !date2) {
      return false;
    }
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  isPreviewTodayDay(date: Date) {
    if (!date) {
      return false;
    }
    return !(date > new Date() || this.sameDay(date, new Date()));
  }

  selectYear(
    year: string,
    month: {
      en: string;
      he: string;
    },
    index: number,
  ) {
    this.valueOfYearSelect = +year;
    this.selectedMonth.year = +year;
    this.selectedMonth.month = month;
    this.selectedMonth.monthNum = this.monthDic.indexOf(month) + 1;
    this.rangeDateToStart = new Date(
      this.selectedMonth.year,
      this.selectedMonth.monthNum - 1,
      1,
    );
    this.rangeDateToStart?.setMonth(
      new Date(
        this.selectedMonth.year,
        this.selectedMonth.monthNum - 1,
        1,
      ).getMonth() - this.countPrev,
    );

    // const maxDate = new Date();
    // maxDate.setFullYear(maxDate.getFullYear() + 20); // 20 years from today
    // maxDate.setMonth(maxDate.getMonth() - 19);

    // if (this.rangeDateToStart < this.minDate) {
    //   this.rangeDateToStart = new Date(this.minDate);
    // }
    // if (this.rangeDateToStart >maxDate) {
    //   this.rangeDateToStart = new Date(maxDate);
    // }

    this.rangeMonthNames = this.generateMonthArray(20);
    this.monthChanged(this.swiperEl.nativeElement.swiper, false);
    this.isYearsToSelectOpen[index] = false;
  }

  selectMonthFromSelect(
    month: {
      en: string;
      he: string;
    },
    index: number,
  ) {
    this.valueOfMonthSelect = month;
    this.selectedMonth.month = month;
    this.selectedMonth.monthNum = this.monthDic.indexOf(month) + 1;
    this.rangeDateToStart = new Date(
      this.selectedMonth.year,
      this.selectedMonth.monthNum - 1,
      1,
    );
    this.rangeDateToStart?.setMonth(
      new Date(
        this.selectedMonth.year,
        this.selectedMonth.monthNum - 1,
        1,
      ).getMonth() - this.countPrev,
    );

    // const maxDate = new Date();
    // maxDate.setFullYear(maxDate.getFullYear() + 20); // 20 years from today
    // maxDate.setMonth(maxDate.getMonth() - 19);

    // if (this.rangeDateToStart < this.minDate) {
    //   this.rangeDateToStart = new Date(this.minDate);
    // }
    // if (this.rangeDateToStart >maxDate) {
    //   this.rangeDateToStart = new Date(maxDate);
    // }

    this.rangeMonthNames = this.generateMonthArray(20);
    this.monthChanged(this.swiperEl.nativeElement.swiper, false);
    this.isMonthsToSelectOpen[index] = false;
  }

  isWithinCustomRange(date: Date): boolean {
    if (this.customDateFrom && this.customDateTo) {
      return date >= this.customDateFrom! && date <= this.customDateTo!;
    }
    return false;
  }

  scrollToSelectedMonth(index: number) {
    setTimeout(() => {
      const monthListElement = this.monthList.nativeElement;
      const activeMonthElement = monthListElement.querySelector('.active');

      if (activeMonthElement) {
        // Calculate the position to scroll so that the active month is centered
        const offsetTop = activeMonthElement.offsetTop;
        const elementHeight = activeMonthElement.clientHeight;
        const containerHeight = monthListElement.clientHeight;

        // Set the scroll position immediately without animation
        monthListElement.scrollTop =
          offsetTop - containerHeight / 2 + elementHeight / 2;
      }
    }, 0);
  }
  scrollToSelectedYear(index: number) {
    setTimeout(() => {
      const yearListElement = this.yearList.nativeElement;
      const activeYearElement = yearListElement.querySelector('.active');

      if (activeYearElement) {
        // Calculate the position to scroll so that the active month is centered
        const offsetTop = activeYearElement.offsetTop;
        const elementHeight = activeYearElement.clientHeight;
        const containerHeight = yearListElement.clientHeight;

        // Set the scroll position immediately without animation
        yearListElement.scrollTop =
          offsetTop - containerHeight / 2 + elementHeight / 2;
      }
    }, 0);
  }
  private getDateOnly(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private isDisabledFromDate(date: Date): boolean {
    if (!this.maxSelectableDate) {
      return false;
    }

    return this.getDateOnly(date) > this.getDateOnly(this.maxSelectableDate);
  }

  private shouldDisableDate(date: Date): boolean {
    const disabledByPreviewToday = this.isPreviewTodayDisable
      ? this.isPreviewTodayDay(date)
      : false;

    const disabledByDateFrom = this.isDisabledFromDate(date);

    return disabledByPreviewToday || disabledByDateFrom;
  }
}
